import React, { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ListaReordenable } from '@/components/ui/ListaReordenable';
import { Textarea } from '@/components/ui/textarea';
import { confirmDialog } from '@/lib/confirmDialog';
import { MAX_LARGO_LEYENDA, MAX_LEYENDAS, leyendaVacia } from '../utils/leyendas';

interface EditorLeyendasProps {
  leyendas: string[];
  onChange: (leyendas: string[]) => void;
  deshabilitado?: boolean;
}

let secuenciaIds = 0;
const nuevoId = () => `leyenda-${++secuenciaIds}`;

const RESUMEN_MAXIMO = 120;
const resumir = (leyenda: string) => {
  const texto = leyenda.trim();
  return texto.length > RESUMEN_MAXIMO ? `${texto.slice(0, RESUMEN_MAXIMO)}…` : texto;
};

/**
 * Leyendas del pie del PDF del presupuesto, que se imprimen numeradas en este
 * orden. Se usa para las predeterminadas y para las de cada presupuesto.
 */
export const EditorLeyendas: React.FC<EditorLeyendasProps> = ({
  leyendas,
  onChange,
  deshabilitado = false,
}) => {
  // Las leyendas son textos sueltos: la lista arrastrable necesita un id
  // estable por fila, que se mantiene alineado con `leyendas`.
  const [ids, setIds] = useState<string[]>(() => leyendas.map(nuevoId));
  let idsVigentes = ids;
  if (ids.length !== leyendas.length) {
    // Cambió desde afuera (restablecer, descartar): se completan o recortan.
    idsVigentes = leyendas.map((_, indice) => ids[indice] ?? nuevoId());
    setIds(idsVigentes);
  }

  const filas = idsVigentes.map((id, indice) => ({ id, indice, texto: leyendas[indice] }));

  const cambiar = (indice: number, texto: string) =>
    onChange(leyendas.map((leyenda, i) => (i === indice ? texto : leyenda)));

  const agregar = () => {
    setIds([...idsVigentes, nuevoId()]);
    onChange([...leyendas, '']);
  };

  const quitar = async (indice: number) => {
    const confirmado = await confirmDialog.open({
      title: `¿Eliminar la leyenda ${indice + 1}?`,
      text: leyendaVacia(leyendas[indice]) ? 'La leyenda está vacía.' : `"${resumir(leyendas[indice])}"`,
      confirmButtonText: 'Eliminar',
    });
    if (!confirmado) return;

    setIds(idsVigentes.filter((_, i) => i !== indice));
    onChange(leyendas.filter((_, i) => i !== indice));
  };

  const reordenar = (nuevosIds: string[]) => {
    const textoPorId = new Map(filas.map((fila) => [fila.id, fila.texto] as const));
    setIds(nuevosIds);
    onChange(nuevosIds.map((id) => textoPorId.get(id) ?? ''));
  };

  return (
    <div className="space-y-3">
      {leyendas.length === 0 ? (
        <p className="text-sm text-muted-foreground">Sin leyendas: el PDF no imprime ninguna.</p>
      ) : (
        <ListaReordenable
          as="ul"
          items={filas}
          onReordenar={reordenar}
          etiquetaItem={(fila) => `leyenda ${fila.indice + 1}`}
          deshabilitado={deshabilitado || leyendas.length < 2}
          className="space-y-3"
        >
          {(fila, asa) => {
            const numero = fila.indice + 1;
            const vacia = leyendaVacia(fila.texto);

            return (
              <div className="flex items-start gap-2 bg-background" data-testid="leyenda">
                <div className="pt-1">{asa}</div>
                <span className="w-6 shrink-0 pt-2 text-right text-sm text-muted-foreground">
                  {numero}.
                </span>
                <div className="flex-1 space-y-1">
                  <Textarea
                    value={fila.texto}
                    onChange={(e) => cambiar(fila.indice, e.target.value)}
                    aria-label={`Leyenda ${numero}`}
                    aria-invalid={vacia || undefined}
                    maxLength={MAX_LARGO_LEYENDA}
                    disabled={deshabilitado}
                  />
                  {vacia && (
                    <p className="text-xs text-destructive">Completá o eliminá esta leyenda.</p>
                  )}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="mt-1 shrink-0"
                  aria-label={`Eliminar leyenda ${numero}`}
                  disabled={deshabilitado}
                  onClick={() => void quitar(fila.indice)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            );
          }}
        </ListaReordenable>
      )}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={agregar}
        disabled={deshabilitado || leyendas.length >= MAX_LEYENDAS}
      >
        <Plus className="mr-2 h-4 w-4" />
        Agregar leyenda
      </Button>
    </div>
  );
};
